import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AuthModal } from "@/components/AuthModal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ArrowLeft, ArrowRight, Loader2, Download, ShieldCheck, FileText, Quote } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, money } from "@/lib/api";
import { toast } from "sonner";

export default function ProductDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [missing, setMissing] = useState(false);
    const [authOpen, setAuthOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => { window.scrollTo(0, 0); api.get(`/products/${id}`).then((r) => setData(r.data)).catch(() => setMissing(true)); }, [id]);

    const buy = async () => {
        setBusy(true);
        try {
            const { data: d } = await api.post("/payments/checkout", { lookup_key: data.product.lookup_key, origin_url: window.location.origin, user_id: user?.id || null });
            window.location.href = d.checkout_url;
        } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Checkout failed"); setBusy(false); }
    };

    if (missing) return <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white text-navy"><p>Product not found.</p><Link to="/#shop" className="font-semibold text-ocean">Back to shop</Link></div>;
    if (!data) return <div className="flex min-h-screen items-center justify-center bg-white"><Loader2 className="h-6 w-6 animate-spin text-ocean" /></div>;
    const { product: p, preview } = data;

    return (
        <div className="min-h-screen bg-white" data-testid="product-detail-page">
            <Header dark onSignIn={() => setAuthOpen(true)} />
            <section className="relative overflow-hidden bg-[#0A1F38] pt-32 pb-20 text-white lg:pt-40">
                <img src={p.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" aria-hidden="true" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F38] via-[#0A1F38]/85 to-[#0A1F38]/40" aria-hidden="true" />
                <div className="relative mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-[1.1fr_.9fr] lg:items-center lg:px-8">
                    <div>
                        <button onClick={() => navigate("/#shop")} data-testid="product-back" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white"><ArrowLeft className="h-4 w-4" /> All digital products</button>
                        <div className="mt-6 flex flex-wrap gap-2"><span className="rounded-full bg-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-teal-light">{p.category}</span><span className="rounded-full bg-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-200">{preview.format}</span></div>
                        <h1 className="mt-5 font-display text-4xl font-semibold tracking-[-0.035em] sm:text-5xl lg:text-6xl" data-testid="product-title">{p.name}</h1>
                        <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-300">{preview.subtitle || p.desc}</p>
                        <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                            <button onClick={buy} disabled={busy} data-testid="product-detail-buy" className="si-btn-primary">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Buy now · {money(p.price)} <ArrowRight className="h-4 w-4" /></>}</button>
                            <div className="flex items-center gap-2 text-sm text-slate-300"><ShieldCheck className="h-4 w-4 text-teal-light" /> Secure Stripe checkout · instant verified delivery</div>
                        </div>
                    </div>
                    <div className="si-hero-widget rounded-3xl p-6" data-testid="product-toc">
                        <div className="flex items-center gap-2 text-sm font-bold"><FileText className="h-4 w-4 text-teal-light" /> What's inside</div>
                        <ol className="mt-4 space-y-2 text-[13.5px] text-slate-200">
                            {preview.toc.map((t, i) => <li key={t} className="flex gap-3"><span className="font-mono text-[11px] text-teal-light">{String(i + 1).padStart(2, "0")}</span>{t}</li>)}
                        </ol>
                    </div>
                </div>
            </section>

            <section className="px-5 py-20 lg:px-8 lg:py-24">
                <div className="mx-auto max-w-7xl">
                    <div className="si-eyebrow">Sample pages</div>
                    <h2 className="mt-4 font-display text-3xl font-semibold tracking-[-0.03em] text-navy sm:text-4xl">Read before you <span className="font-serif-accent text-ocean">buy.</span></h2>
                    <div className="mt-10 grid gap-6 lg:grid-cols-3">
                        {preview.samples.map((s, i) => (
                            <article key={s.title} className="si-shop-card relative p-7" data-testid={`product-sample-${i}`}>
                                <Quote className="h-6 w-6 text-teal/40" />
                                <div className="mt-4 text-[11px] font-bold uppercase tracking-[0.14em] text-teal">{s.title}</div>
                                <p className="mt-3 text-[15px] leading-relaxed text-slate-700">{s.body}</p>
                            </article>
                        ))}
                    </div>
                    <p className="mt-6 text-xs text-slate-500">Excerpts are from the actual product files. Educational use only; not legal, tax, or investment advice.</p>
                </div>
            </section>

            <section className="border-t border-slate-200 bg-slate-50 px-5 py-20 lg:px-8">
                <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[1fr_1.2fr]">
                    <div>
                        <div className="si-eyebrow">Questions</div>
                        <h2 className="mt-4 font-display text-3xl font-semibold tracking-[-0.03em] text-navy sm:text-4xl">Good to know</h2>
                        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
                            <div className="flex items-center gap-2 font-semibold text-navy"><Download className="h-4 w-4 text-teal" /> Delivery</div>
                            <p className="mt-2 text-sm text-slate-600">Your download link appears right after verified payment and is emailed to you. Files are yours to keep.</p>
                            <button onClick={buy} disabled={busy} data-testid="product-detail-buy-2" className="mt-5 inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-navy text-sm font-semibold text-white hover:bg-ocean">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Buy now · {money(p.price)}</>}</button>
                        </div>
                    </div>
                    <Accordion type="single" collapsible className="overflow-hidden rounded-3xl border border-slate-200 bg-white" data-testid="product-faq">
                        {preview.faqs.map(([q, a], i) => (
                            <AccordionItem key={q} value={`f${i}`} className="border-slate-200 px-6 last:border-b-0">
                                <AccordionTrigger className="py-5 text-left font-display text-[16px] font-semibold text-navy hover:no-underline">{q}</AccordionTrigger>
                                <AccordionContent className="pb-5 text-[15px] leading-relaxed text-slate-600">{a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>
            <Footer />
            <AuthModal open={authOpen} onOpenChange={setAuthOpen} onSuccess={() => setAuthOpen(false)} />
        </div>
    );
}
